import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { AdminUserRecord } from '@/lib/types'

const ADMIN_USERS_KEY = ['admin-users'] as const

export function useAdminUsers(): ReturnType<typeof useQuery<AdminUserRecord[]>> {
  return useQuery({
    queryKey: ADMIN_USERS_KEY,
    queryFn: () => apiClient.get<AdminUserRecord[]>(endpoints.adminUsers.list),
  })
}

interface AddAdminPayload {
  email: string
  name?: string
  role: 'admin' | 'viewer'
}

export function useAddAdminUser(): ReturnType<
  typeof useMutation<unknown, unknown, AddAdminPayload>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AddAdminPayload) => apiClient.post(endpoints.adminUsers.create, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
    },
  })
}

export function useRemoveAdminUser(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del(endpoints.adminUsers.remove(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
    },
  })
}

interface ChangeRolePayload {
  id: string
  role: 'admin' | 'viewer'
}

export function useChangeAdminRole(): ReturnType<
  typeof useMutation<unknown, unknown, ChangeRolePayload>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: ChangeRolePayload) =>
      apiClient.patch(endpoints.adminUsers.changeRole(id), { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
    },
  })
}
